import React from 'react';

interface PaymentRequest {
  id: number;
  customer_name: string;
  operation_token?: string | null;
  shop_process_id?: string | null;
  amount: number;
  reserved_amount: number;
  currency: string;
  status: string;
  created_at: string;
  expires_at: string;
}

interface PaymentRequestDetailProps {
  request: PaymentRequest;
  onClose: () => void;
  onConfirm?: () => void;
  onReject?: () => void;
}

const PaymentRequestDetail: React.FC<PaymentRequestDetailProps> = ({
  request,
  onClose,
  onConfirm,
  onReject
}) => {
  const formatAmount = (amount: number) => {
    return amount.toString();
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      'RUB': '₽',
      'KZT': '₸',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currency] || currency;
  };

  const formatStatus = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string; bgColor: string } } = {
      'pending': { 
        text: 'Ожидает оплаты', 
        color: 'text-yellow-800', 
        bgColor: 'bg-yellow-100' 
      },
      'confirmed': { 
        text: 'Подтвержден', 
        color: 'text-green-800', 
        bgColor: 'bg-green-100' 
      },
      'expired': { 
        text: 'Истек', 
        color: 'text-red-800', 
        bgColor: 'bg-red-100' 
      },
      'rejected': { 
        text: 'Отклонен', 
        color: 'text-red-800', 
        bgColor: 'bg-red-100' 
      }
    };
    
    const statusInfo = statusMap[status] || { 
      text: status, 
      color: 'text-gray-800', 
      bgColor: 'bg-gray-100' 
    };
    
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusInfo.bgColor} ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  const externalId = request.shop_process_id || request.operation_token || null;
  const displayId = externalId || `#${request.id}`;
  const isExpired = new Date(request.expires_at) < new Date();
  const timeUntilExpiry = new Date(request.expires_at).getTime() - new Date().getTime();
  const minutesUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60));

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Заголовок */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">
              Заявка {displayId.startsWith('#') ? displayId : `#${displayId}`}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Основная информация */}
          <div className="space-y-6">
            {/* Статус и время */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                {formatStatus(request.status)}
                {isExpired && (
                  <span className="text-red-600 text-sm font-medium">Истекла</span>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Создана</div>
                <div className="text-sm font-medium text-gray-900">
                  {new Date(request.created_at).toLocaleString('ru-RU')}
                </div>
              </div>
            </div>

            {/* Покупатель */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Покупатель</h4>
              <div className="text-lg font-medium text-gray-900">{request.customer_name}</div>
            </div>

            {/* Сумма */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-700 mb-2">Сумма платежа</h4>
              <div className="text-2xl font-bold text-blue-900">
                {formatAmount(request.amount)} {getCurrencySymbol(request.currency)}
              </div>
              {request.reserved_amount !== request.amount && (
                <div className="mt-2 text-sm text-blue-600">
                  Резерв: {formatAmount(request.reserved_amount)} {getCurrencySymbol(request.currency)}
                </div>
              )}
            </div>

            {/* Время истечения */}
            <div className="bg-orange-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-orange-700 mb-2">Время истечения</h4>
              <div className="text-lg font-medium text-orange-900">
                {new Date(request.expires_at).toLocaleString('ru-RU')}
              </div>
              {!isExpired && (
                <div className="mt-2 text-sm text-orange-600">
                  Осталось: {minutesUntilExpiry} мин
                </div>
              )}
            </div>

            {/* Дополнительная информация */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">ID заявки</h4>
                <div className="text-lg font-mono text-gray-900">
                  {displayId.startsWith('#') ? displayId : `#${displayId}`}
                </div>
                {externalId && (
                  <div className="text-xs text-gray-500 mt-1">
                    внутр. #{request.id}
                  </div>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Валюта</h4>
                <div className="text-lg font-medium text-gray-900">
                  {request.currency} {getCurrencySymbol(request.currency)}
                </div>
              </div>
              {externalId && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">ID магазина</h4>
                  <div className="text-lg font-mono text-gray-900">{externalId}</div>
                </div>
              )}
            </div>
          </div>

          {/* Действия */}
          <div className="mt-8 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Закрыть
            </button>
            
            {request.status === 'pending' && !isExpired && (
              <>
                <button
                  onClick={onReject}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Отклонить
                </button>
                <button
                  onClick={onConfirm}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Подтвердить
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestDetail;
